import React, { useEffect, useMemo, useState } from 'react';
import { withRouter } from 'react-router';
import { connect } from 'react-redux';
import Select from 'react-select';
import ClipboardJS from "clipboard";
import { toast } from 'react-toastify';
import swal from "sweetalert2";
import { Tab, TabList, TabPanel, Tabs } from "react-tabs";
import withReactContent from "sweetalert2-react-content";
import moment from "moment";
import axios from '../../../../axios-instance';
import {
  deleteIframe,
  getAvailableEvents,
  getIframeById,
  updateIframe,
} from '../../../../store/actions/promoterActions';
import CheckBox from '../../../../components/UI/Checkbox/CheckBox';
import FieldLabel from '../../../../components/FieldLabel/FieldLabel';
import ColorPicker from '../../../../components/ColorPicker/ColorPicker';
import { getDefaultStyles } from '../../../../helpers/getDefaultStyles';
import NewLoader from '../../../../components/NewLoader/NewLoader';
import IframePreview from './components/IframePreview';
import { getCustomURLs } from '../../../../helpers/getCustomURLs';
import ROUTES from '../../../../constants/routes';
import { getHeaders } from '../../../../helpers/getHeaders';
import { fontsOptions } from '../../../../constants/fontOptions';

import "react-tabs/style/react-tabs.css";

const MySwal = withReactContent(swal);

export const Iframe = ({
  getIframeById,
  token,
  loading,
  eventsLoading,
  getAvailableEvents,
  updateIframe,
  deleteIframe,
  availableEvents,
  ...props
}) => {
  const isNewIframe = props.match.params.iframeId === "new";
  const {
    primaryColor,
    primarySemiLightColor,
    fontFamily,
    backgroundColor,
    textColor,
  } = getDefaultStyles(document.getElementById('body'));

  const [iframe, setIframe] = useState({
    eventIds: null,
    fontFamily: fontFamily,
    primaryColor: primaryColor,
    backgroundColor: backgroundColor,
    dayColor: backgroundColor,
    primaryTextColor: textColor,
    secondaryTextColor: "#ffffff",
    hoverColor: primarySemiLightColor,
    listVersion: 0,
    name: "",
  });
  const [isIframeEdited, setIframeEdited] = useState(false);

  const getData = async () => {
    const iframeId = props.match.params.iframeId;

    if (availableEvents.length === 0) {
      await getAvailableEvents(token);
    }

    if (iframeId !== 'new') {
      const ifarmeData = await getIframeById(iframeId, token);

      setIframe({
        ...ifarmeData,
        fontFamily: ifarmeData.fontFamily || fontFamily,
        primaryColor: ifarmeData.primaryColor || primaryColor,
        backgroundColor: ifarmeData.backgroundColor || backgroundColor,
        dayColor: ifarmeData.dayColor || backgroundColor,
        primaryTextColor: ifarmeData.primaryTextColor || textColor,
        secondaryTextColor: ifarmeData.secondaryTextColor || "#ffffff",
        hoverColor: ifarmeData.hoverColor || primarySemiLightColor,
        eventIds:
          ifarmeData.returnEventsToSell ? availableEvents.map(e => e.id) : ifarmeData.eventIds,
      });
    } else {
      setIframe(prev => ({
        ...prev,
        eventIds: [],
        returnEventsToSell: false,
      }))
    }
  };

  const createIframe = async () => {
    try {
      await axios.post(`/iFrame`, iframe, {headers: getHeaders(token)});

      toast.success("Iframe successfully created");
      props.history.push({
        pathname: ROUTES.PROMOTER_IFRAMES,
      });

    } catch (error) {
      toast.error("Iframe not received");
    }
  }

  const toggleEvent = (eventId) => {
    setIframeEdited(true);
    setIframe((prev) => {
      const isIdExist = prev.eventIds.some((id) => id === eventId);
      let eventIds;

      if (isIdExist) {
        eventIds = prev.eventIds.filter((id) => id !== eventId);
      } else {
        eventIds = [...prev.eventIds, eventId];
      }

      return { ...prev, eventIds };
    });
  };

  const handleChangeSetting = (name, value) => {
    setIframeEdited(true);
    setIframe((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (name === 'returnEventsToSell' && value) {
      setIframe((prev) => ({
        ...prev,
        eventIds: availableEvents.map(ev => ev.id),
      }));
    }
  };

  const handleConfirm = () => {
    setIframeEdited(false);
    if (isNewIframe) {
      createIframe();
    } else {
      updateIframe(iframe, token);
    }
  };

  const handleDeleteIframe = async () => {
    const result = await deleteIframe(iframe.id, token);

    if (result) {
      props.history.push({
        pathname: ROUTES.PROMOTER_IFRAMES,
      });
    }
  };

  const code = useMemo(() => {
    const { PUBLIC_URL } = getCustomURLs();

    return `
      <iframe
        id='eventsList'
        title='Events list'
        style="
          border: none;
          background: transparent;
          width: 100%;
          height: 100%;
          min-width: ${iframe.listVersion === 0 ? '370px' : '300px'};
          min-height: ${iframe.listVersion === 0 ? '400px' : '280px'};
        "
        src='${PUBLIC_URL}/iframes/${iframe.slug}'
      ></iframe>
    `
  }, [iframe])

  const copyIframeComponentCode = () => {
    if (isIframeEdited) {
      MySwal.fire({
        title: "Attention",
        text: "Please save iFrame to apply the latest changes",
        customClass: 'swal2-modal',
        showCancelButton: true,
        confirmButtonText: "Save",
        reverseButtons: true,
      }).then((result) => {
        if (result.value) {
          handleConfirm();
        }
      });

      return;
    }

    ClipboardJS.copy(code);

    toast.dismiss(1);
    toast.success("Iframe code is copied to clipboard", {
      toastId: 1,
    });
  };

  const eventsWithFullInfo = useMemo(() => {
    if (iframe.eventIds) {
      const eventsList = iframe.eventIds.map((eventId) => {
        return availableEvents.find((event) => event.id === eventId);
      });
  
      return eventsList.sort((ev1, ev2) => {
        const time1 = new Date(ev1.startDate).getTime();
        const time2 = new Date(ev2.startDate).getTime();
  
        return time1 - time2;
      });
    }
    
    return null;
  }, [iframe, availableEvents]);

  useEffect(() => {
    getData();
  }, []);

  if ((loading && !isNewIframe) || eventsLoading) {
    return <NewLoader />;
  }

  return (
    <div className='iframe'>
      <div div className="add-event__block">
        <label>Iframe name</label>
        <input
          type="text"
          name="name"
          value={iframe.name}
          placeholder="Enter iframe name"
          maxLength="500"
          onChange={(e) => {
            handleChangeSetting('name', e.target.value);
          }}
        />
      </div>

      <div className='iframe__content'>
        <article className='iframe__events'>
          <h3 className='iframe__subtitle'>Active events</h3>

          <div className='iframe__event-select-type'>
            <label className='radiobutton'>
              <input
                className='radiobutton__input'
                name='eventSelectType'
                type='radio'
                value='manually'
                onChange={(e) => {
                  handleChangeSetting('returnEventsToSell', false);
                }}
                checked={'returnEventsToSell' in iframe && !iframe.returnEventsToSell}
              />
              <span>Add events manually</span>
            </label>

            <label className='radiobutton'>
              <input
                className='radiobutton__input'
                name='eventSelectType'
                type='radio'
                value='auto'
                onChange={(e) => {
                  handleChangeSetting('returnEventsToSell', true);
                }}
                checked={'returnEventsToSell' in iframe && iframe.returnEventsToSell}
              />
              <span>Add all events</span>
            </label>
          </div>

          {'returnEventsToSell' in iframe && iframe.returnEventsToSell && (
            <div>All available events will be added automatically</div>
          )}

          {'returnEventsToSell' in iframe && !iframe.returnEventsToSell && iframe.eventIds && (
            <div className='iframe__events-list'>
              {availableEvents.map((event) => {
                return (
                  <CheckBox
                    key={event.id}
                    name='is-selected'
                    id={`is-selected-${event.id}`}
                    checked={iframe.eventIds.some((id) => id === event.id)}
                    onChange={() => toggleEvent(event.id)}
                  >
                    <div className='iframe__events-name'>
                      {event.name}
                      <span>{moment(event.startDate).format("DD.MM.YYYY hh:mm")}</span>
                    </div>
                  </CheckBox>
                );
              })}
            </div>
          )}
        </article>

        <article className='iframe__settings'>
          <h3 className='iframe__subtitle'>Settings</h3>

          <div className='iframe__event-select-type'>
            <label className='radiobutton'>
              <input
                className='radiobutton__input'
                name='listVersion'
                type='radio'
                value={0}
                onChange={(e) => {
                  handleChangeSetting('listVersion', 0);
                }}
                checked={iframe.listVersion === 0}
              />
              <span>Large list</span>
            </label>

            <label className='radiobutton'>
              <input
                className='radiobutton__input'
                name='listVersion'
                type='radio'
                value={1}
                onChange={(e) => {
                  handleChangeSetting('listVersion', 1);
                }}
                checked={iframe.listVersion === 1}
              />
              <span>Agenda</span>
            </label>
          </div>

          <FieldLabel className='control-field' label='Font family'>
            <Select
              options={fontsOptions}
              value={
                fontsOptions.find(
                  ({ value }) => value === iframe.fontFamily
                ) || { value: fontFamily, label: fontFamily }
              }
              onChange={(value) => {
                handleChangeSetting('fontFamily', value.value);
              }}
              classNamePrefix='custom-select'
              className='custom-select'
            />
          </FieldLabel>

          <FieldLabel className='control-field' label='Primary text color'>
            <ColorPicker
              colorShowType={'circle'}
              value={iframe.primaryTextColor}
              onChange={(color) => handleChangeSetting('primaryTextColor', color)}
              inputType='input'
              resetDefaultButton={false}
            />
          </FieldLabel>

          <FieldLabel className='control-field' label='Secondary text color'>
            <ColorPicker
              colorShowType={'circle'}
              value={iframe.secondaryTextColor}
              onChange={(color) => handleChangeSetting('secondaryTextColor', color)}
              inputType='input'
              resetDefaultButton={false}
            />
          </FieldLabel>

          <FieldLabel className='control-field' label='Background color'>
            <ColorPicker
              colorShowType={'circle'}
              value={iframe.backgroundColor}
              onChange={(color) =>
                handleChangeSetting('backgroundColor', color)
              }
              inputType='input'
              resetDefaultButton={false}
            />
          </FieldLabel>

          <FieldLabel className='control-field' label='Primary color'>
            <ColorPicker
              colorShowType={'circle'}
              value={iframe.primaryColor}
              onChange={(color) => handleChangeSetting('primaryColor', color)}
              inputType='input'
              resetDefaultButton={false}
            />
          </FieldLabel>

          {iframe.listVersion === 0 && (
            <FieldLabel className='control-field' label='Day color'>
              <ColorPicker
                colorShowType={'circle'}
                value={iframe.dayColor}
                onChange={(color) => handleChangeSetting('dayColor', color)}
                inputType='input'
                resetDefaultButton={false}
              />
            </FieldLabel>
          )}

          <FieldLabel className='control-field' label='Hover color'>
            <ColorPicker
              colorShowType={'circle'}
              value={iframe.hoverColor}
              onChange={(color) => handleChangeSetting('hoverColor', color)}
              inputType='input'
              resetDefaultButton={false}
            />
          </FieldLabel>
        </article>

        <article className='iframe__preview'>
          <Tabs className="iframe__tabs">
            <TabList>
              <Tab>Preview</Tab>
              /
              <Tab>Code</Tab>
            </TabList>
            <TabPanel>
              <div className="iframe__large-list-wrapper">
                {iframe.eventIds && iframe.eventIds.length === 0 && (
                  <div>No active events</div>
                )}
                {eventsWithFullInfo && eventsWithFullInfo.length !== 0 && (
                  <IframePreview localIframeData={{...iframe, events: eventsWithFullInfo}} />
                )}
              </div>
            </TabPanel>
            <TabPanel>
              <div className='iframe__code-container'>{code}</div>
            </TabPanel>
          </Tabs>
        </article>
      </div>

      <div className='iframe__actions'>
        {!isNewIframe && (
          <>
            <button
              type='button'
              className='btn-primary btn-primary--copy'
              onClick={copyIframeComponentCode}
            >
              Copy code
            </button>

            <button
              className="btn-red-outline"
              type="button"
              onClick={handleDeleteIframe}
            >
              Delete
            </button>
          </>
        )}

        <button
          type='button'
          className='btn-primary'
          onClick={handleConfirm}
          disabled={iframe.name.length === 0}
        >
          {props.match.params.iframeId === "new" ? "Create" : "Save"}
        </button>
      </div>
    </div>
  );
};

const mapStateToProps = ({ auth, promoter }) => ({
  auth,
  token: auth.user.token,
  loading: promoter.loading,
  eventsLoading: promoter.eventsLoading,
  availableEvents: promoter.availableEvents,
});

const mapDispatchToProps = (dispatch) => ({
  getIframeById: (iframeId, token) => dispatch(getIframeById(iframeId, token)),
  updateIframe: (data, token) => dispatch(updateIframe(data, token)),
  deleteIframe: (iframeId, token) => dispatch(deleteIframe(iframeId, token)),
  getAvailableEvents: (token) => dispatch(getAvailableEvents(token)),
});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(Iframe));
